import { useState, useEffect, useCallback, useMemo } from 'react';
import { useWeb3React } from '@web3-react/core';
import { ethers } from 'ethers';
import { isSupportedNetwork } from '../config/contracts';

export interface BalanceState {
  balance: string;
  formattedBalance: string;
  isLoading: boolean;
  error: string | null;
}

export const useBalance = (): BalanceState => {
  const { account, provider, chainId, isActive } = useWeb3React();
  const [balance, setBalance] = useState('0');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = useCallback(async () => {
    if (!account || !provider || !chainId || !isSupportedNetwork(chainId)) {
      setBalance('0');
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const balanceWei = await provider.getBalance(account);
      const balanceEth = ethers.utils.formatEther(balanceWei);
      setBalance(balanceEth);
    } catch (err: any) {
      console.error('Failed to fetch balance:', err);
      setError('Failed to fetch balance');
      setBalance('0');
    } finally {
      setIsLoading(false);
    }
  }, [account, provider, chainId]);

  useEffect(() => {
    if (isActive) {
      fetchBalance();
      // Update balance every 30 seconds
      const interval = setInterval(fetchBalance, 30000);
      return () => clearInterval(interval);
    } else {
      setBalance('0');
      setError(null);
    }
  }, [isActive, fetchBalance]);

  const formattedBalance = useMemo(() => {
    return parseFloat(balance).toFixed(4);
  }, [balance]);

  return {
    balance,
    formattedBalance,
    isLoading,
    error,
  };
};

export default useBalance;